import { useCallback } from 'react';
import useVelocityClient from 'src/hooks/useVelocityClient';
import invariant from 'tiny-invariant';
import { createCancelOrdersIx } from '@velocity-exchange/common';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import { useGetTxParams } from 'src/hooks/velocityActions/transactions/useGetTxParams';
import { useTxErrorHandler } from 'src/hooks/velocityActions/transactions/useTxErrorHandler';
import { runOrderActionToast } from 'src/hooks/velocityActions/transactions/runOrderActionToast';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useAutoSignVelocityActionWithFallback } from 'src/hooks/wallets/useAutoSignVelocityActionWithFallback';

export const useCancelOrders = () => {
	const velocityClient = useVelocityClient();
	const currentUserAccount = useCurrentUserAccount();
	const getTxParams = useGetTxParams();
	const handleTxError = useTxErrorHandler();

	const cancelOrders = useAutoSignVelocityActionWithFallback(
		useCallback(
			async (
				signerWallet: UiEnhancedIWalletV2,
				isAutoSigned: boolean,
				toastId: string,
				...orderIds: number[]
			) => {
				await runOrderActionToast({
					toastId,
					isAutoSigned,
					pendingMessage: `Cancelling order${
						orderIds.length > 1 ? 's' : ''
					}...`,
					run: async () => {
						invariant(velocityClient, 'driftClient is required');
						invariant(currentUserAccount?.client, 'user client is required');

						const ix = await createCancelOrdersIx({
							velocityClient: velocityClient,
							user: currentUserAccount.client,
							orderIds,
							mainSignerOverride: signerWallet.publicKey,
						});

						const txParams = getTxParams();
						await signerWallet.signAndSendTransaction(ix, txParams);
					},
					success: {
						message: `Order${orderIds.length > 1 ? 's' : ''} cancelled`,
					},
					handleError: handleTxError,
					errorLogLabel: 'Error canceling orders',
				});
			},
			[velocityClient, currentUserAccount, getTxParams, handleTxError]
		)
	);

	const cancelAllOrders = useCallback(async () => {
		invariant(currentUserAccount?.client, 'user client is required');

		const currentUserOpenOrders = currentUserAccount.client.getOpenOrders();
		const ids = currentUserOpenOrders.map((order) => order.orderId);
		if (!ids.length) {
			return;
		}

		const toastId = `cancel-all-orders-${ids.join(',')}-${Date.now()}`;
		return cancelOrders(toastId, ...ids);
	}, [cancelOrders, currentUserAccount]);

	return { cancelOrders, cancelAllOrders };
};
